/**
 * Same-origin redirect guard.
 *
 * react-router 6.30.x carried an open-redirect advisory
 * (GHSA-wrjc-x8rr-h8h6) where a backslash-prefixed path such as `/\evil.com`
 * — or a scheme-relative/absolute URL — could become an external destination.
 * The app now uses a patched 7.x release, and this guard remains as defense in
 * depth before post-auth redirect targets reach the router.
 *
 * Returns the path only when it is unambiguously a same-origin relative path,
 * otherwise the fallback.
 */
export function safeRedirectPath(raw: string | null | undefined, fallback = "/dashboard"): string {
  if (!raw) return fallback;

  // Decode once so encoded separators (%2f, %5c) cannot smuggle a host through.
  let candidate = raw;
  try {
    candidate = decodeURIComponent(raw);
  } catch {
    return fallback;
  }

  // Control characters (incl. \t \n \r) are stripped by browsers when parsing
  // URLs, so they can be used to hide a scheme: "j\navascript:".
  // eslint-disable-next-line no-control-regex
  candidate = candidate.replace(/[\u0000-\u001F\u007F]/g, "");

  if (!candidate.startsWith("/")) return fallback;
  // "//host" and "/\host" (and "\\", after normalization) are host-relative.
  if (/^[/\\]{2}/.test(candidate)) return fallback;
  if (candidate.startsWith("/\\")) return fallback;
  // Backslashes anywhere have no legitimate use in our route paths.
  if (candidate.includes("\\")) return fallback;

  return candidate;
}
